import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthRequest } from 'Src/feature/auth-request/useAuthRequest';
import { useCurrentAuthRequest } from 'Src/feature/auth-request/useCurrentAuthRequest';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';

import { useTerminal } from './useTerminal';

const sectionId = 'TerminalUiChangeTracker';

declare global {
  export interface GlobalEventHandlersEventMap {
    'TerminalUiChangeTracker.autoLogOut.override': CustomEvent<{ isPause: boolean; reason: string }>;
  }
}

interface TerminalUiChangeTrackerProps {
  isReady: boolean;
}

/**
 * TerminalUiChangeTracker
 * @description - Component that controls the Auto-logout logic.
 * It stays permanently mounted and uses `isReady` programmatically to avoid state loss.
 */
const TerminalUiChangeTracker: FC<TerminalUiChangeTrackerProps> = ({ isReady }) => {
  const logger = useLogger();
  const log = useMemo(() => logger.getSubLogger({ name: sectionId }), [logger]);
  const { t } = useTranslation();
  const { navigate, crumbs } = useRoute();
  const { currentRequest } = useCurrentAuthRequest();
  const toast = useToast();

  const authRequestAnswer = useAuthRequest.mutate.answer();
  const terminalLocation = useTerminal.query.get();

  const timerRef = useRef<NodeJS.Timeout>(undefined);
  const pausedReasons = useRef(new Set<string>());

  const onTimerEnd = useCallback(() => {
    log.info('onTimerEnd()', 'logging out...');
    if (currentRequest && currentRequest?.id) {
      authRequestAnswer.mutate({
        authRequestId: currentRequest.id,
        outcome: 'Canceled',
      }, {
        onError: (error) => {
          toast.show.serviceError(error, { id: `${sectionId}.authRequest-cancel`, title: t('authRequest.errors.cancel') });
        },
      });
    }
    navigate(crumbs.pos.user.logout.path);
  }, [authRequestAnswer, crumbs.pos.user.logout.path, currentRequest, log, navigate, t, toast.show]);

  // Use a ref for the callback so updateTimer doesn't recreate on every currentRequest change
  const onTimerEndRef = useRef(onTimerEnd);
  onTimerEndRef.current = onTimerEnd;

  const autoLogoutAfterSecs = terminalLocation.data?.terminal.terminalSettings.autoLogoutAfterSecs;

  const updateTimer = useCallback(() => {
    // Only proceed if terminal is ready and we have data
    if (!isReady || autoLogoutAfterSecs === undefined) {
      if (timerRef.current) {
        log.debug('updateTimer()', 'clearing timer - terminal not ready');
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      return;
    }

    if (autoLogoutAfterSecs === 0) {
      log.debug('updateTimer()', 'skipping - autoLogoutAfterSecs is 0');
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      return;
    }

    clearTimeout(timerRef.current);

    if (pausedReasons.current.size > 0) {
      log.debug('updateTimer()', 'timer paused', { reasons: Array.from(pausedReasons.current) });
      return;
    }

    log.debug('updateTimer()', 'timer started', { seconds: autoLogoutAfterSecs });
    timerRef.current = setTimeout(() => onTimerEndRef.current(), autoLogoutAfterSecs * 1000);
  }, [isReady, log, autoLogoutAfterSecs]);

  useDOMEventListener('click', () => {
    updateTimer();
  });
  useDOMEventListener('touchstart', () => {
    updateTimer();
  });
  useDOMEventListener('TerminalUiChangeTracker.autoLogOut.override', (evt) => {
    const { isPause, reason } = evt.detail;
    if (isPause) {
      pausedReasons.current.add(reason);
    } else {
      pausedReasons.current.delete(reason);
    }
    log.info('TerminalUiChangeTracker.autoLogOut.override', { isPause, reason, activeReasons: Array.from(pausedReasons.current) });
    updateTimer();
  });

  useEffect(() => {
    // 1. On Mount: Starts the timer if the terminal is ready.
    // 2. On transition to 'Ready': Starts/Restarts the timer from the beginning.
    // 3. On transition to 'Not Ready': Clears the timer immediately.
    // 4. On Setting Change: Restarts the timer with the new threshold (if ready).

    if (isReady && autoLogoutAfterSecs !== undefined) {
      updateTimer();
    } else {
      if (timerRef.current) {
        log.debug('useEffect lifecycle', 'clearing timer - terminal not ready or no settings');
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    }

    return () => {
      // Cleanup on actual unmount of the component
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isReady, autoLogoutAfterSecs, updateTimer, log]);

  return <></>;
};

export { TerminalUiChangeTracker };
